import React, { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "react-query";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  TextField,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Tooltip,
  Popover,
  Collapse,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import QuestionAnswerOutlined from "@mui/icons-material/QuestionAnswerOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";
import { usePageContext } from "../../../context/PageContext";
import { useAuthContext } from "../../../context/AuthContext";
import { fetchData } from "../../../api/client";
import { useChatbotMutations } from "../../../hooks/useChatbot";
import { ChatbotFlowEditor } from "../../../components/ChatbotFlow";
import ChatbotTreeView from "../../../components/ChatbotFlow/ChatbotTreeView";
import ChatbotNodePanel from "../../../components/ChatbotFlow/ChatbotNodePanel";
import ChatbotSimulator from "../../../components/ChatbotFlow/ChatbotSimulator";
import EmptyStateWithAI from "../../../components/EmptyStateWithAI";
import { useTranslation } from "react-i18next";

export default function ChatbotConfig() {
  const { t } = useTranslation("settings");
  const { userData } = useAuthContext();
  const { setCurrentPage } = usePageContext();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [botUsers, setBotUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [error, setError] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [creatingBot, setCreatingBot] = useState(false);
  const [newBotName, setNewBotName] = useState("");
  const [openCreateBot, setOpenCreateBot] = useState(false);
  const [openEditBot, setOpenEditBot] = useState(false);
  const [editingBot, setEditingBot] = useState(false);
  const [editBotName, setEditBotName] = useState("");
  const [openDeleteBot, setOpenDeleteBot] = useState(false);
  const [deletingBot, setDeletingBot] = useState(false);
  const [creatingWithAI, setCreatingWithAI] = useState(false);
  const [viewMode, setViewMode] = useState("canvas"); // "canvas" | "tree"
  const [selectedSlotTree, setSelectedSlotTree] = useState(null);
  const [isTreePanelOpen, setIsTreePanelOpen] = useState(false);

  const selectedBotUser = botUsers.find((u) => String(u?.id ?? "") === String(selectedUserId));

  const loadBotUsers = useCallback(() => {
    let cancelled = false;
    setLoadingUsers(true);
    fetchData("/users?limit=200&tipo_responsavel=chatbots", "GET")
      .then((res) => {
        if (cancelled) return;
        const data = res?.data ?? res ?? [];
        setBotUsers(Array.isArray(data) ? data : []);
        if ((data?.length || 0) > 0) {
          // Mantém seleção se ainda existir; senão seleciona o primeiro.
          const currentId = String(selectedUserId || "");
          const stillExists = currentId && data.some((u) => String(u?.id ?? "") === currentId);
          if (!stillExists) setSelectedUserId(String(data[0]?.id ?? ""));
        } else {
          setSelectedUserId("");
        }
      })
      .catch(() => {
        if (!cancelled) setError(t("chatbotConfigPage.loadUsersError"));
      })
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedUserId, t]);

  useEffect(() => {
    setCurrentPage({ title: "ChatBot", showBackButton: false });
  }, [setCurrentPage]);

  useEffect(() => {
    const cleanup = loadBotUsers();
    return cleanup;
  }, [loadBotUsers]);

  const userId = selectedUserId ? parseInt(selectedUserId, 10) : null;
  const { createSlot, updateSlot, deleteSlot } = useChatbotMutations(userId);

  const createDefaultStructure = useCallback(async (targetUserId) => {
    await fetchData("/chatbot/slots/generate-initial", "POST", {
      user_id: targetUserId,
    });
  }, []);

  const handleCreateBotUser = async () => {
    if (!newBotName.trim()) {
      setError(t("chatbotConfigPage.nameRequired"));
      return;
    }
    setCreatingBot(true);
    try {
      setError(null);
      await fetchData("/users", "POST", {
        nome: newBotName.trim(),
        status: 1,
        ura: 1,
        incluir_nome: 0,
        // Alguns workspaces exigem auditoria no create (mesmo que o handler aceite null)
        id_usuario: userData?.id ?? null,
        id_usuario_ultima_alteracao: userData?.id ?? null,
      });
      setOpenCreateBot(false);
      setNewBotName("");
      loadBotUsers();
    } catch (err) {
      const baseMsg =
        err?.message ||
        err?.error ||
        (Array.isArray(err?.message) && err.message[0]) ||
        t("chatbotConfigPage.errorCreateUser");
      const detail =
        (typeof err?.data === "string" && err.data) ||
        (typeof err?.details === "string" && err.details) ||
        null;
      const msg = detail ? `${baseMsg} (${detail})` : baseMsg;
      setError(msg);
    } finally {
      setCreatingBot(false);
    }
  };

  const handleCreateBotUserWithAI = async () => {
    setCreatingWithAI(true);
    try {
      setError(null);

      const name = `ChatBot ${new Date().toLocaleDateString("pt-BR")}`;
      const created = await fetchData("/users", "POST", {
        nome: name,
        status: 1,
        ura: 1,
        incluir_nome: 0,
        id_usuario: userData?.id ?? null,
        id_usuario_ultima_alteracao: userData?.id ?? null,
      });

      const createdUserId = created?.data?.id ?? created?.id;

      if (createdUserId) {
        // Importante: cria os slots antes de selecionar o usuário,
        // para evitar que o primeiro GET dos slots rode antes da criação e fique cacheado por ~30s.
        await createDefaultStructure(createdUserId);
        queryClient.invalidateQueries(["chatbot", "slots", createdUserId]);
        queryClient.invalidateQueries(["chatbot"]);
        setSelectedUserId(String(createdUserId));
        loadBotUsers();
      } else {
        loadBotUsers();
      }
    } catch (err) {
      const baseMsg =
        err?.message ||
        err?.error ||
        (Array.isArray(err?.message) && err.message[0]) ||
        t("chatbotConfigPage.errorCreateAI");
      const detail =
        (typeof err?.data === "string" && err.data) ||
        (typeof err?.details === "string" && err.details) ||
        null;
      setError(detail ? `${baseMsg} (${detail})` : baseMsg);
    } finally {
      setCreatingWithAI(false);
    }
  };

  const handleOpenEdit = () => {
    if (!selectedBotUser) return;
    setEditBotName(selectedBotUser?.nome ?? "");
    setOpenEditBot(true);
  };

  const handleEditBotUser = async () => {
    if (!selectedBotUser) return;
    if (!editBotName.trim()) {
      setError(t("chatbotConfigPage.nameRequired"));
      return;
    }
    setEditingBot(true);
    try {
      setError(null);
      await fetchData(`/users/${selectedBotUser.id}`, "PUT", {
        nome: editBotName.trim(),
        id_usuario_ultima_alteracao: userData?.id ?? null,
      });
      setOpenEditBot(false);
      loadBotUsers();
    } catch (err) {
      const baseMsg =
        err?.message ||
        err?.error ||
        (Array.isArray(err?.message) && err.message[0]) ||
        t("chatbotConfigPage.errorEditUser");
      const detail =
        (typeof err?.data === "string" && err.data) ||
        (typeof err?.details === "string" && err.details) ||
        null;
      const msg = detail ? `${baseMsg} (${detail})` : baseMsg;
      setError(msg);
    } finally {
      setEditingBot(false);
    }
  };

  const handleDeleteBotUser = async () => {
    if (!selectedBotUser) return;
    setDeletingBot(true);
    try {
      setError(null);
      await fetchData(`/users/${selectedBotUser.id}`, "DELETE");
      setOpenDeleteBot(false);
      loadBotUsers();
    } catch (err) {
      const baseMsg =
        err?.message ||
        err?.error ||
        (Array.isArray(err?.message) && err.message[0]) ||
        t("chatbotConfigPage.errorDeleteUser");
      const detail =
        (typeof err?.data === "string" && err.data) ||
        (typeof err?.details === "string" && err.details) ||
        null;
      const msg = detail ? `${baseMsg} (${detail})` : baseMsg;
      setError(msg);
    } finally {
      setDeletingBot(false);
    }
  };

  const handleEmptyInit = useCallback(() => {
    if (!userId) return;
    setError(null);
    createSlot.mutate(
      { user_id: userId, parent_id: 0, identificador: "Início", criar_excecao: true },
      {
        onSuccess: () => setError(null),
        onError: (err) => setError(err?.message || t("chatbotConfigPage.errorInit")),
      }
    );
  }, [userId, createSlot, t]);

  const [legendAnchor, setLegendAnchor] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const handleTreeSaveSlot = (payload) => {
    if (!payload?.id) return;
    updateSlot.mutate(payload, {
      onSuccess: () => {
        setError(null);
        setIsTreePanelOpen(false);
      },
      onError: (err) => setError(err?.message || t("chatbotConfigPage.errorSaveSlot")),
    });
  };

  const handleTreeDeleteSlot = (id) => {
    if (!id) return;
    deleteSlot.mutate(id, {
      onSuccess: () => {
        setError(null);
        setIsTreePanelOpen(false);
        setSelectedSlotTree(null);
      },
      onError: (err) => setError(err?.message || t("chatbotConfigPage.errorDeleteSlot")),
    });
  };

  const handleTreeAddChild = (parentSlot) => {
    if (!parentSlot || !userId) return;
    createSlot.mutate(
      {
        user_id: userId,
        parent_id: parentSlot.id,
        identificador: "Novo passo",
        criar_excecao: true,
        global: 0,
        ignorarglobal: 0,
      },
      {
        onSuccess: () => setError(null),
        onError: (err) => setError(err?.message || t("chatbotConfigPage.errorCreateSubstep")),
      }
    );
  };

  return (
    <Box
      sx={{
        pt: 1,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Cabeçalho padrão das telas de Configurações */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" component="h1">
          {t("chatbotConfigPage.heading")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 1, alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: 220 }} variant="outlined">
            <InputLabel>{t("chatbotConfigPage.chatbotUserLabel")}</InputLabel>
            <Select
              label={t("chatbotConfigPage.chatbotUserLabel")}
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              disabled={loadingUsers}
            >
              {botUsers.map((u) => (
                <MenuItem key={u.id} value={String(u.id)}>
                  {u.nome} {u.email ? `(${u.email})` : ""}
                </MenuItem>
              ))}
              {!loadingUsers && botUsers.length === 0 && (
                <MenuItem disabled>{t("chatbotConfigPage.noBotUserFound")}</MenuItem>
              )}
            </Select>
          </FormControl>
          <Tooltip title={t("chatbotConfigPage.editNameTooltip")}>
            <span>
              <IconButton
                size="small"
                onClick={handleOpenEdit}
                disabled={loadingUsers || !selectedUserId}
                aria-label={t("chatbotConfigPage.editNameTooltip")}
              >
                <EditOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t("chatbotConfigPage.deleteTooltip")}>
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={() => setOpenDeleteBot(true)}
                disabled={loadingUsers || !selectedUserId}
                aria-label={t("chatbotConfigPage.deleteTooltip")}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenCreateBot(true)}
            sx={{ textTransform: "none" }}
            disabled={loadingUsers}
          >
            {t("chatbotConfigPage.newChatbot")}
          </Button>
        </Box>
      </Box>

      {/* Área de conteúdo com fundo branco, seguindo padrão das outras telas */}
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 1,
          p: 2,
          boxShadow: (t) => t.shadows[1],
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Barra interna: legenda + tabs + ajuda */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 1,
            minHeight: 40,
          }}
        >
          <Tooltip title={t("chatbotConfigPage.legendTooltip")}>
            <IconButton size="small" onClick={(e) => setLegendAnchor(e.currentTarget)} aria-label={t("chatbotConfigPage.legendTitle")}>
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Popover
            open={!!legendAnchor}
            anchorEl={legendAnchor}
            onClose={() => setLegendAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <Box sx={{ p: 2, maxWidth: 420 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                {t("chatbotConfigPage.legendTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {t("chatbotConfigPage.legendBody")}
              </Typography>
            </Box>
          </Popover>
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
            <Tabs
              value={viewMode}
              onChange={(_, v) => setViewMode(v)}
              textColor="primary"
              indicatorColor="primary"
              sx={{
                minHeight: 32,
                "& .MuiTab-root": { minHeight: 32, textTransform: "none", fontSize: 13, px: 1.5 },
              }}
            >
              <Tab label={t("chatbotConfigPage.tabCanvas")} value="canvas" />
              <Tab label={t("chatbotConfigPage.tabTree")} value="tree" />
            </Tabs>
            <Button
              size="small"
              endIcon={showHelp ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setShowHelp((v) => !v)}
              sx={{ textTransform: "none" }}
            >
              {t("chatbotConfigPage.learnMore")}
            </Button>
          </Box>
        </Box>

      <Collapse in={showHelp}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.5 }}>
          {t("chatbotConfigPage.helpCanvas")}
        </Typography>
      </Collapse>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {!selectedUserId && !loadingUsers && (
        <Box sx={{ mt: 1, mb: 2 }}>
          <EmptyStateWithAI
            title={t("chatbotConfigPage.emptyTitle")}
            description={t("chatbotConfigPage.emptyDescription")}
            primaryLabel={t("chatbotConfigPage.createWithAI")}
            secondaryLabel={t("chatbotConfigPage.createManually")}
            onPrimaryClick={handleCreateBotUserWithAI}
            onSecondaryClick={() => setOpenCreateBot(true)}
            primaryLoading={creatingWithAI}
            icon={<SmartToyOutlined sx={{ fontSize: 56 }} color="primary" />}
            maxWidth="920px"
          />
        </Box>
      )}

      {selectedUserId && (
        <>
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            {viewMode === "canvas" ? (
              <ChatbotFlowEditor userId={userId} onEmptyInit={handleEmptyInit} />
            ) : (
              <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
                <Box sx={{ flex: 1.2, minWidth: 260, pr: 1.5 }}>
                  <ChatbotTreeView
                    userId={userId}
                    selectedSlotId={selectedSlotTree?.id ?? null}
                    onSelectSlot={(slot) => {
                      setSelectedSlotTree(slot);
                      setIsTreePanelOpen(true);
                    }}
                    onAddChild={handleTreeAddChild}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 260 }}>
                  <ChatbotNodePanel
                    slot={isTreePanelOpen ? selectedSlotTree : null}
                    onClose={() => setIsTreePanelOpen(false)}
                    onSave={handleTreeSaveSlot}
                    onDelete={handleTreeDeleteSlot}
                    onAddSubpasso={
                      selectedSlotTree && selectedSlotTree.identificador !== "Exceção"
                        ? () => handleTreeAddChild(selectedSlotTree)
                        : undefined
                    }
                    isSaving={updateSlot.isLoading}
                    isDeleting={deleteSlot.isLoading}
                    isAddingSubpasso={createSlot.isLoading}
                    saveError={null}
                    canDelete={
                      !!selectedSlotTree &&
                      selectedSlotTree.identificador !== "Exceção" &&
                      selectedSlotTree.identificador !== "Início"
                    }
                  />
                </Box>
              </Box>
            )}
          </Box>
          {/* Simulador flutuante, independente do layout Canvas/Árvore */}
          <ChatbotSimulator userId={userId} />
        </>
      )}

      </Box>

      <Dialog open={openCreateBot} onClose={() => !creatingBot && setOpenCreateBot(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t("chatbotConfigPage.newChatbot")}</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label={t("chatbotConfigPage.chatbotName")}
            fullWidth
            value={newBotName}
            onChange={(e) => setNewBotName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateBot(false)} disabled={creatingBot}>
            {t("shared.cancel")}
          </Button>
          <Button onClick={handleCreateBotUser} disabled={creatingBot} variant="contained">
            {t("chatbotConfigPage.create")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEditBot} onClose={() => !editingBot && setOpenEditBot(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t("chatbotConfigPage.editNameTitle")}</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label={t("chatbotConfigPage.chatbotName")}
            fullWidth
            value={editBotName}
            onChange={(e) => setEditBotName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditBot(false)} disabled={editingBot}>
            {t("shared.cancel")}
          </Button>
          <Button onClick={handleEditBotUser} disabled={editingBot} variant="contained">
            {t("shared.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteBot} onClose={() => !deletingBot && setOpenDeleteBot(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t("chatbotConfigPage.deleteTitle")}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" icon={<QuestionAnswerOutlined />}>
            {t("chatbotConfigPage.deleteConfirm", { name: selectedBotUser?.nome ?? "" })}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteBot(false)} disabled={deletingBot}>
            {t("shared.cancel")}
          </Button>
          <Button onClick={handleDeleteBotUser} disabled={deletingBot} variant="contained" color="error">
            {t("shared.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
